/**
 * useDubbingState Hook
 *
 * Manages all dubbing-related state using useReducer for better organization.
 */

import { useReducer, useCallback, useMemo } from 'react';
import type { MediaType } from '@/components/common/MediaUploader';
import type { AudioLanguageConfig } from '@/components/common/LanguageSelector';
import type { AudioTrackMode, DubbingProgress, DubbingResponse, LLMProvider } from '@/types';
import type { AudioDubbingResponse } from '@/services/dubbing';
import type { DubbingState, DubbingAction, DubbingStatus } from '../types';

const initialState: DubbingState = {
  // Media
  mediaFile: null,
  mediaType: null,
  mediaUrl: null,
  mediaDuration: null,

  // Languages
  sourceLanguage: '', // Empty/'auto' = auto-detect (AUDIO dubbing only; video requires an explicit language)
  targetLanguages: [],
  voiceConfigs: [],

  // Options
  audioTrackMode: 'add',

  // LLM Refinement
  enableLlmRefinement: false,
  llmProvider: 'openrouter',
  llmModel: '',
  availableModels: [],

  // Progress
  uploadProgress: 0,
  dubbingProgress: null,

  // Results
  taskId: null,
  downloadUrl: null,
  localOutputPath: null,
  dubbingResult: null,
  audioDubbingResult: null,

  // Status
  status: 'idle',
  error: null,
};

function dubbingReducer(state: DubbingState, action: DubbingAction): DubbingState {
  switch (action.type) {
    case 'SET_MEDIA':
      return {
        ...state,
        mediaFile: action.file,
        mediaType: action.mediaType,
        dubbingResult: null,
        audioDubbingResult: null,
        dubbingProgress: null,
      };

    case 'SET_MEDIA_URL':
      return { ...state, mediaUrl: action.url };

    case 'SET_MEDIA_DURATION':
      return { ...state, mediaDuration: action.duration };

    case 'CLEAR_MEDIA':
      return {
        ...state,
        mediaFile: null,
        mediaType: null,
        mediaUrl: null,
        mediaDuration: null,
        uploadProgress: 0,
      };

    case 'SET_SOURCE_LANGUAGE':
      return { ...state, sourceLanguage: action.language };

    case 'SET_VOICE_CONFIGS':
      return {
        ...state,
        voiceConfigs: action.configs,
        targetLanguages: action.configs.map((c) => c.language),
      };

    case 'SET_AUDIO_TRACK_MODE':
      return { ...state, audioTrackMode: action.mode };

    case 'SET_LLM_REFINEMENT':
      return { ...state, enableLlmRefinement: action.enabled };

    case 'SET_LLM_PROVIDER':
      return { ...state, llmProvider: action.provider };

    case 'SET_LLM_MODEL':
      return { ...state, llmModel: action.model };

    case 'SET_AVAILABLE_MODELS':
      return { ...state, availableModels: action.models };

    case 'SET_UPLOAD_PROGRESS':
      return { ...state, uploadProgress: action.progress };

    case 'SET_DUBBING_PROGRESS':
      return { ...state, dubbingProgress: action.progress };

    case 'SET_TASK_ID':
      return { ...state, taskId: action.taskId };

    case 'SET_DOWNLOAD_URL':
      return { ...state, downloadUrl: action.url };

    case 'SET_LOCAL_OUTPUT_PATH':
      return { ...state, localOutputPath: action.path };

    case 'SET_DUBBING_RESULT':
      return {
        ...state,
        dubbingResult: action.result,
        status: 'completed',
      };

    case 'SET_AUDIO_DUBBING_RESULT':
      return {
        ...state,
        audioDubbingResult: action.result,
        status: 'completed',
      };

    case 'SET_STATUS':
      return {
        ...state,
        status: action.status,
        error: action.error ?? null,
      };

    case 'START_UPLOADING':
      return {
        ...state,
        status: 'uploading',
        uploadProgress: 0,
      };

    case 'START_DUBBING':
      return {
        ...state,
        status: 'dubbing',
        dubbingProgress: null,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

export function useDubbingState() {
  const [state, dispatch] = useReducer(dubbingReducer, initialState);

  // Action creators
  const setMedia = useCallback((file: File, mediaType: MediaType) => {
    dispatch({ type: 'SET_MEDIA', file, mediaType });
  }, []);

  const setMediaUrl = useCallback((url: string) => {
    dispatch({ type: 'SET_MEDIA_URL', url });
  }, []);

  const setMediaDuration = useCallback((duration: number) => {
    dispatch({ type: 'SET_MEDIA_DURATION', duration });
  }, []);

  const clearMedia = useCallback(() => {
    dispatch({ type: 'CLEAR_MEDIA' });
  }, []);

  const setSourceLanguage = useCallback((language: string) => {
    dispatch({ type: 'SET_SOURCE_LANGUAGE', language });
  }, []);

  const setVoiceConfigs = useCallback((configs: AudioLanguageConfig[]) => {
    dispatch({ type: 'SET_VOICE_CONFIGS', configs });
  }, []);

  const setAudioTrackMode = useCallback((mode: AudioTrackMode) => {
    dispatch({ type: 'SET_AUDIO_TRACK_MODE', mode });
  }, []);

  const setLlmRefinement = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_LLM_REFINEMENT', enabled });
  }, []);

  const setLlmProvider = useCallback((provider: LLMProvider) => {
    dispatch({ type: 'SET_LLM_PROVIDER', provider });
  }, []);

  const setLlmModel = useCallback((model: string) => {
    dispatch({ type: 'SET_LLM_MODEL', model });
  }, []);

  const setAvailableModels = useCallback(
    (models: Array<{ label: string; value: string; disabled?: boolean }>) => {
      dispatch({ type: 'SET_AVAILABLE_MODELS', models });
    },
    []
  );

  const setUploadProgress = useCallback((progress: number) => {
    dispatch({ type: 'SET_UPLOAD_PROGRESS', progress });
  }, []);

  const setDubbingProgress = useCallback((progress: DubbingProgress | null) => {
    dispatch({ type: 'SET_DUBBING_PROGRESS', progress });
  }, []);

  const setTaskId = useCallback((taskId: string) => {
    dispatch({ type: 'SET_TASK_ID', taskId });
  }, []);

  const setLocalOutputPath = useCallback((path: string | null) => {
    dispatch({ type: 'SET_LOCAL_OUTPUT_PATH', path });
  }, []);

  const setDownloadUrl = useCallback((url: string | null) => {
    dispatch({ type: 'SET_DOWNLOAD_URL', url });
  }, []);

  const setDubbingResult = useCallback((result: DubbingResponse) => {
    dispatch({ type: 'SET_DUBBING_RESULT', result });
  }, []);

  const setAudioDubbingResult = useCallback((result: AudioDubbingResponse) => {
    dispatch({ type: 'SET_AUDIO_DUBBING_RESULT', result });
  }, []);

  const setStatus = useCallback((status: DubbingStatus, error?: string) => {
    dispatch({ type: 'SET_STATUS', status, error });
  }, []);

  const startUploading = useCallback(() => {
    dispatch({ type: 'START_UPLOADING' });
  }, []);

  const startDubbing = useCallback(() => {
    dispatch({ type: 'START_DUBBING' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // Memoize actions object to prevent infinite loops in useEffect dependencies
  // Without this, the actions object is recreated on every render, causing
  // useEffects that depend on it to run infinitely
  const actions = useMemo(
    () => ({
      setMedia,
      setMediaUrl,
      setMediaDuration,
      clearMedia,
      setSourceLanguage,
      setVoiceConfigs,
      setAudioTrackMode,
      setLlmRefinement,
      setLlmProvider,
      setLlmModel,
      setAvailableModels,
      setUploadProgress,
      setDubbingProgress,
      setTaskId,
      setDownloadUrl,
      setLocalOutputPath,
      setDubbingResult,
      setAudioDubbingResult,
      setStatus,
      startUploading,
      startDubbing,
      reset,
    }),
    [
      setMedia,
      setMediaUrl,
      setMediaDuration,
      clearMedia,
      setSourceLanguage,
      setVoiceConfigs,
      setAudioTrackMode,
      setLlmRefinement,
      setLlmProvider,
      setLlmModel,
      setAvailableModels,
      setUploadProgress,
      setDubbingProgress,
      setTaskId,
      setDownloadUrl,
      setLocalOutputPath,
      setDubbingResult,
      setAudioDubbingResult,
      setStatus,
      startUploading,
      startDubbing,
      reset,
    ]
  );

  return {
    state,
    actions,
  };
}
